import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { PopupAd } from "@/types";
import { getActivePopupAds, trackPopupImpression } from "@/services/publicApi";
import { pageKeyFromPath } from "@/config/popupPages";

const STORAGE_PREFIX = "myp_popup_seen_";

/** How many times this visitor has already seen a given popup. */
function seenCount(id: string): number {
  try {
    return Number(window.localStorage.getItem(`${STORAGE_PREFIX}${id}`) || "0");
  } catch {
    return 0;
  }
}

/** Records one more view of a popup for this visitor. */
function bumpSeen(id: string): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${id}`, String(seenCount(id) + 1));
  } catch {
    /* storage unavailable — the popup simply repeats next time */
  }
}

function isWithinWindow(ad: PopupAd, now: number): boolean {
  if (ad.startAt && new Date(ad.startAt).getTime() > now) return false;
  if (ad.endAt && new Date(ad.endAt).getTime() < now) return false;
  return true;
}

/**
 * The centre-screen popup ad (lightbox). Mounted globally, it opens the highest
 * priority popup that matches the current page, is inside its date window and
 * has not yet hit its per-visitor frequency cap. Dismissable via the close
 * button, the backdrop or the Escape key.
 */
export default function PopupAdLightbox() {
  const { pathname } = useLocation();
  const [ads, setAds] = useState<PopupAd[]>([]);
  const [current, setCurrent] = useState<PopupAd | null>(null);
  const [visible, setVisible] = useState(false);

  // Load the live popups once per session.
  useEffect(() => {
    let active = true;
    getActivePopupAds()
      .then((list) => {
        if (active) setAds(list);
      })
      .catch(() => {
        if (active) setAds([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Pick + schedule a popup whenever the page or the popup list changes.
  useEffect(() => {
    setVisible(false);
    setCurrent(null);
    if (!ads.length) return undefined;

    const pageKey = pageKeyFromPath(pathname);
    const now = Date.now();
    const picked =
      ads
        .filter((ad) => ad.isActive && ad.mediaUrl)
        .filter((ad) => ad.targetPages.includes("all") || ad.targetPages.includes(pageKey))
        .filter((ad) => isWithinWindow(ad, now))
        .filter((ad) => ad.frequencyCap <= 0 || seenCount(ad.id) < ad.frequencyCap)
        .sort((a, b) => b.priority - a.priority)[0] || null;

    if (!picked) return undefined;

    const timer = setTimeout(() => {
      bumpSeen(picked.id);
      trackPopupImpression(picked.id);
      setCurrent(picked);
      setVisible(true);
    }, Math.max(0, picked.delaySeconds) * 1000);

    return () => clearTimeout(timer);
  }, [ads, pathname]);

  // Escape closes the open popup.
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVisible(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  if (!current || !visible) return null;

  const close = () => setVisible(false);
  const ctaLabel = current.linkLabel || "Learn more";
  const isExternal = current.linkUrl.startsWith("http");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={current.title}
    >
      <button
        type="button"
        aria-label="Close popup"
        onClick={close}
        className="absolute inset-0 cursor-default bg-foreground-950/70 backdrop-blur-sm"
      ></button>

      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-lg border border-background-200 bg-background-50 animate-fade-up">
        <button
          type="button"
          onClick={close}
          aria-label="Close popup"
          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/70 text-background-50 transition-colors hover:bg-foreground-950 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="w-full bg-foreground-950">
          {current.mediaType === "video" ? (
            <video
              src={current.mediaUrl}
              className="block h-auto max-h-[60vh] w-full object-contain"
              autoPlay
              muted
              loop
              playsInline
              controls
            />
          ) : (
            <img
              src={current.mediaUrl}
              alt={current.title}
              title={current.title}
              className="block h-auto max-h-[60vh] w-full object-cover object-top"
            />
          )}
        </div>

        <div className="p-5">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-secondary-900">
            <i className="ri-advertisement-line"></i> Sponsored
          </span>
          <h3 className="mt-2.5 font-heading text-lg font-bold text-foreground-950">{current.title}</h3>
          {current.linkUrl &&
            (isExternal ? (
              <a
                href={current.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                {ctaLabel}
                <i className="ri-arrow-right-line"></i>
              </a>
            ) : (
              <Link
                to={current.linkUrl}
                onClick={close}
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                {ctaLabel}
                <i className="ri-arrow-right-line"></i>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}